import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { Money } from '@principle-theorem/accounting';
import {
  formControlChanges$,
  IsoDateSelectorComponent,
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  resolveClaimItems,
  ServiceProviderHandler,
  toClaimableItems,
} from '@principle-theorem/principle-core';
import {
  IClaimableItem,
  IHealthcareClaim,
  IInvoice,
  IMohExtendedData,
  IMohReconciledClaimItem,
  ITransaction,
  ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  ISODateType,
  shareReplayCold,
  snapshot,
  toISODate,
  WithRef,
} from '@principle-theorem/shared';
import { isNumber, sum } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IMohApproveClaimDialogData {
  invoice: WithRef<IInvoice>;
  claim: IHealthcareClaim;
  transaction: WithRef<ITransaction<IMohExtendedData>>;
}

interface IMohApproveClaimForm {
  items: IMohReconciledClaimItem[];
  paymentDate: ISODateType;
}

@Component({
  selector: 'pr-moh-approve-claim-dialog',
  imports: [
    NgDialogModule,
    MatListModule,
    IsoDateSelectorComponent,
    MatCheckboxModule,
  ],
  templateUrl: './moh-approve-claim-dialog.component.html',
  styleUrl: './moh-approve-claim-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MohApproveClaimDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<MohApproveClaimDialogComponent, IMohExtendedData>>(
      MatDialogRef
    );
  data = inject<IMohApproveClaimDialogData>(MAT_DIALOG_DATA);
  form = new TypedFormGroup<IMohApproveClaimForm>({
    items: new TypedFormControl<IMohReconciledClaimItem[]>([]),
    paymentDate: new TypedFormControl<ISODateType>(),
  });
  today = moment();
  amountPayable$: Observable<number>;
  items$: Observable<IMohReconciledClaimItem[]>;
  extendedData: IMohExtendedData;

  constructor() {
    this.extendedData = this.data.transaction.extendedData ?? {
      amountClaimed: this.data.transaction.amount,
      items: this._getClaimableItems(),
    };

    this.form.setValue({
      items: this.extendedData.items.map((item) => ({
        ...item,
        amountPayable: item.serviceCode.amount,
        approved: true,
      })),
      paymentDate: toISODate(this.today),
    });

    this.items$ = formControlChanges$(this.form.controls.items).pipe(
      map((claimItems) => claimItems ?? []),
      shareReplayCold()
    );

    this.amountPayable$ = this.items$.pipe(
      takeUntilDestroyed(),
      map((claimItems) =>
        Money.amount(sum(claimItems.map((item) => item.amountPayable)))
      ),
      shareReplayCold()
    );
  }

  updateAmountPayable(claimItem: IMohReconciledClaimItem, event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    const currentItems = this.form.controls.items.value;
    if (!isNumber(value) || !currentItems) {
      return;
    }

    const updatedItems = currentItems.map((item) => {
      if (item.serviceCode.uid !== claimItem.serviceCode.uid) {
        return item;
      }
      return { ...item, amountPayable: Money.amount(value) };
    });
    this.form.controls.items.setValue(updatedItems);
  }

  toggleApproval(claimItem: IMohReconciledClaimItem): void {
    const currentItems = this.form.controls.items.value;
    if (!currentItems) {
      return;
    }

    const updatedItems = currentItems.map((item) => {
      if (item.serviceCode.uid !== claimItem.serviceCode.uid) {
        return item;
      }

      const isNowApproved = !item.approved;
      return {
        ...item,
        approved: isNowApproved,
        amountPayable: isNowApproved ? item.serviceCode.amount : 0,
      };
    });
    this.form.controls.items.setValue(updatedItems);
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const amountPayable = await snapshot(this.amountPayable$);
    this._dialogRef.close({
      ...this.extendedData,
      ...this.form.getRawValue(),
      amountPayable,
    });
  }

  private _getClaimableItems(): IClaimableItem[] {
    return toClaimableItems(
      resolveClaimItems(this.data.invoice, this.data.claim)
    ).filter((item) => {
      const serviceCode = ServiceProviderHandler.findServiceCode(
        item.serviceCode.code
      );
      return serviceCode?.type === ServiceCodeType.MOH;
    });
  }
}
